import { cx } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import { useCallback, useState, type ReactNode, type Ref } from 'react'

import {
  DISABLE_EDD_FOR_AGE_RESTRICTED_PRODUCTS,
  EDDMessageContainerDS,
} from '@src/components/blocks/edd-message-container-ds'
import { useLocaleContext } from '@src/components/locale/locale.context'
import { STATSIG_EXPERIMENTS_CONFIG } from '@src/config/statsig-experiments.config'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { STATSIG_EXPERIMENTS, useStatsigExperiment } from '@src/experiments/statsig-integration'
import { useStatsigDynamicConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-config.hook'
import { useStatsigDynamicHolidayConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-holiday-config.hook'
import { ExpressPay } from '@src/modules/express-pay'
import { useIsExpressPayEnabled } from '@src/modules/express-pay/express-pay'
import { BUTTON_TYPES } from '@src/quince-components/src/atoms/button'
import { ProductCTAConstants } from '@src/quince-components/src/atoms/productCTA/constants'
import { getBaseMessagingConditions, getTrackingObject } from '@src/quince-components/src/atoms/productCTA/productCTA'
import { CTA_TYPE } from '@src/quince-components/src/atoms/productCTA/productCTAConstants'
import type { ProductCTAType } from '@src/quince-components/src/atoms/productCTA/types'
import { useGoldBarProductEligibility } from '@src/quince-components/src/atoms/productCTA/useGoldBarProductEligibility'
import { getIsGiftCard } from '@src/quince-components/src/atoms/productCTA/utils'
import { PURCHASE_TYPES } from '@src/quince-components/src/atoms/purchaseTypeSelector/PurchaseTypeSelector'
import WaitlistSmsModal from '@src/quince-components/src/atoms/waitlist-sms-modal'
import { QUINCE_BOT_LOCATORS } from '@src/quince-site-common/src/constants/quinceBot'
import useStorefrontModeContext from '@src/quince-site-common/src/contexts/storefront-mode-context'
import Product from '@src/quince-site-common/src/domains/Product'
import productHelper, { CUSTOM_FIELD_NAMES } from '@src/quince-site-common/src/helpers/productHelper'
import { useCart } from '@src/quince-site-common/src/hooks/cart'
import useATCVsATBExperiment from '@src/quince-site-common/src/hooks/use-atc-vs-atb-experiment'
import { useWaitlistSmsModal } from '@src/quince-site-common/src/hooks/use-waitlist-sms-modal'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import { ANALYTIC_COOKIES } from '@src/quince-site-common/src/utils/constants'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import validateEmail from '@src/quince-site-common/src/utils/validateEmail'
import { SIMILAR_PRODUCTS_SECTION_NAME_FOR_REF } from '@src/quince-site-pdp/src/organisms/productPageSections/similarProducts/constant'

import {
  buttonWrapperCss,
  defaultCTAHiddenCss,
  expressPayRowCss,
  expressPayRowReadyCss,
  hideKlarnaCss,
  pointerEventsNoneClassCss,
  pointerEventsNoneCss,
  postOrderCtaWrapperCss,
  quickATCWrapperCss,
} from './product-cta-ds.styles'
import { ProductDisplayButtonDS } from './product-display-button-ds'
import {
  HolidayMessageDS,
  InStockAndReadyToShipDS,
  NonPhysicalProductDeliveryTextDS,
  RenderErrorMessageDS,
} from './product-message-ds'
import { ProductWaitlistCTADS } from './product-waitlist-cta-ds'

import type { SystemStyleObject } from '@lastbrand/web-ui-styled-system/types'

/**
 * Holiday-message helpers mirrored verbatim from the module-private helpers in legacy productCTA.tsx
 * (`hasValidDisplayTags` / `checkHolidayMessageShown`). They are not exported from the legacy module,
 * so they are reproduced here exactly — same conditions, same return shape — to keep the
 * holiday-message gating and the EDD `holidayMessageShown` signal in parity with legacy.
 */
const hasValidDisplayTags = (
  displayTags: string[] | undefined,
  holidayConfig: ReturnType<typeof useStatsigDynamicHolidayConfig> | null,
): boolean => {
  return !!(displayTags && Array.isArray(displayTags) && displayTags.length > 0 && holidayConfig?.tags)
}

const checkHolidayMessageShown = (
  displayTags: string[],
  holidayConfig: ReturnType<typeof useStatsigDynamicHolidayConfig> | null,
  locale: string,
): boolean => {
  return displayTags.some(displayTag => {
    const tagConfig = holidayConfig?.tags.find(tag => tag.tag === displayTag && tag.enabled)
    if (tagConfig?.message) {
      const message = tagConfig.message[locale] ?? Object.values(tagConfig.message)[0] ?? null
      return !!message
    }
    return false
  })
}

/**
 * ProductCTADS — pure Design-System twin of `ProductCTAWrapper` (default export of legacy
 * productCTA.tsx). Rendered only on the DS path; prop-compatible with `ProductCTAType` so it is a
 * drop-in for `ProductCTAWrapper`.
 *
 * Logic is reused verbatim from the shared hooks/utils — `useProductCTAType` resolves the CTA type
 * upstream and is passed in via `props.type` exactly as the legacy wrapper receives it, while
 * `useGoldBarProductEligibility`, `useATCVsATBExperiment`, `useWaitlistSmsModal`,
 * `useTrackIsElementVisible`, `getTrackingObject`, `CTA_TYPE` and `ProductCTAConstants` are imported,
 * never reimplemented.
 *
 * EDD note: the EDD region (`EDDMessageContainer`) is owned by another agent. We render the legacy
 * `EDDMessageContainer` import as-is in the same slot the legacy wrapper used; no EDD logic is
 * touched here.
 */
const ProductCTADS = (props: ProductCTAType) => {
  const {
    selectedVariantPurchaseInfo,
    onJoinWaitList,
    showVariantAvailableInventory,
    product,
    inStockMsgClass,
    areAllVariantOOS = false,
    expressPayConfig,
    isCurrentProductOptionLeavingSoon,
    disableAddToCart,
    showNewPdpDesignV2,
    isQuickATCModal,
    isFromCartPopup,
    handleShopInApp,
    enableExpressPayAnimation,
    hideKlarna,
    purchaseOption,
    selectedPlanId,
    showPurchaseTypeSelector,
  } = props

  // `ProductCTAType` types `type`/`addToCartRef` as `any`; narrow locally so downstream stays type-safe.
  const type = props.type as CTA_TYPE
  const addToCartRef = props.addToCartRef as Ref<HTMLButtonElement> | undefined

  const { t } = useTranslation('common')
  const { isB2BMode } = useStorefrontModeContext()
  const showATB = useATCVsATBExperiment()
  const atcLabel = showATB ? ProductCTAConstants.ADD_TO_BAG : ProductCTAConstants.ADD_TO_CART
  const selectedVariantId = selectedVariantPurchaseInfo?.selectedVariantId
  const isNonPhysicalProduct = product
    ? productHelper.getIsTruthyCustomField(product, CUSTOM_FIELD_NAMES.IS_NON_PHYSICAL_PRODUCT)
    : false

  const [email, setEmail] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const { isBisWaitlistSmsEnabled, showSmsModal, setShowSmsModal, openSmsModalIfAllowed, onSubmitPhone } =
    useWaitlistSmsModal({ product, selectedOptions: props.selectedOptions })

  const { locale } = useLocaleContext()
  const eddExp = useStatsigExperiment(STATSIG_EXPERIMENTS.EDD)
  const disableForceEDD = useStatsigDynamicConfig<boolean>(DISABLE_EDD_FOR_AGE_RESTRICTED_PRODUCTS)
  const isEDDExpEnabled =
    // force edd for age restricted products as per existing logic
    eddExp.experimentBucket !== STATSIG_EXPERIMENTS_CONFIG[STATSIG_EXPERIMENTS.EDD].buckets?.control ||
    (product && productHelper.getIsAgeRestrictedProduct(product) && !disableForceEDD) // force edd for age restricted products

  const { isGoldBarError } = useGoldBarProductEligibility(product, setErrorMessage)

  // In-stock "ready to ship" messaging — reuses the shared `getBaseMessagingConditions` helper
  // verbatim and mirrors legacy ProductCTA's gating (skip when EDD exp is on or a holiday message
  // shows, except in the post-order flow where it is always shown).
  const baseMessagingConditions = getBaseMessagingConditions(
    selectedVariantPurchaseInfo,
    showVariantAvailableInventory,
    product,
    areAllVariantOOS,
    isGoldBarError,
    eddExp.init,
  )
  const holidayConfig = useStatsigDynamicHolidayConfig()
  const hasDisplayTags = hasValidDisplayTags(selectedVariantPurchaseInfo?.eddDetail?.displayTags, holidayConfig)

  let isHolidayMessageShown = false
  if (baseMessagingConditions && hasDisplayTags && selectedVariantPurchaseInfo?.eddDetail?.displayTags) {
    isHolidayMessageShown = checkHolidayMessageShown(
      selectedVariantPurchaseInfo.eddDetail.displayTags,
      holidayConfig,
      locale,
    )
  }

  const normalFlowShowsInStockMessage = baseMessagingConditions && !isEDDExpEnabled && !isHolidayMessageShown
  const showInStockAndReadyToShip = props.isPostOrderFlow ? true : normalFlowShowsInStockMessage

  const { isBuyNowInProgress } = useCart()
  const isExpressPayEnabled = useIsExpressPayEnabled()
  const showExpressPay = expressPayConfig && !expressPayConfig.disabled && isExpressPayEnabled
  const [isApplePayReady, setIsApplePayReady] = useState(false)

  const handleApplePayReady = useCallback(() => {
    setIsApplePayReady(true)
  }, [])

  const EMAIL_ERROR_MSG = t('please_enter_valid_email_address')
  const ctaText = t('preorder')

  // Reset invalid-email message when the input is absent (parity with legacy revertEmailErrorMsg).
  const revertEmailErrorMsg = () => {
    if (errorMessage === EMAIL_ERROR_MSG) {
      setErrorMessage('')
    }
  }

  // Telemetry reused verbatim: preorder_button / waitlist_button IS_VISIBLE impressions.
  const { ref: trackIsVisiblePreorderRef } = useTrackIsElementVisible(
    useCallback(() => getTrackingObject('preorder_button', selectedVariantId), [selectedVariantId]),
  )
  const { ref: trackIsVisibleWaitlistRef } = useTrackIsElementVisible(
    useCallback(() => getTrackingObject('waitlist_button', selectedVariantId), [selectedVariantId]),
  )

  const handleAddToCart = (isPreorder = false, planId?: number) => {
    // eslint-disable-next-line
    props.onAddToCart(isPreorder, planId)
  }

  const handleClick = () => {
    switch (type) {
      case CTA_TYPE.PREORDER:
        handleAddToCart(true)
        break

      case CTA_TYPE.WAITLIST:
        if (isCurrentProductOptionLeavingSoon) {
          // Fires the parent's `clicked_shop_similar` BUTTON_CLICK telemetry (context label SHOP_SIMILAR).
          props.onClickedShopSimilar?.()
          const element = document.querySelector(`[data-section-name="${SIMILAR_PRODUCTS_SECTION_NAME_FOR_REF}"]`)
          if (element) {
            // @i18n-ignore
            element.scrollIntoView({ behavior: 'smooth' })
          }
          return
        }
        if (selectedVariantPurchaseInfo?.hasJoinedWaitList) {
          return
        }
        if (AuthUtil.isLoggedIn()) {
          const cookieEmail =
            getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL) ??
            getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_TENTATIVE_EMAIL) ??
            ''
          onJoinWaitList?.(cookieEmail)
          openSmsModalIfAllowed(cookieEmail)
        } else if (validateEmail(email)) {
          onJoinWaitList?.(email)
          setErrorMessage('')
          setEmail('')
          openSmsModalIfAllowed(email)
        } else {
          setErrorMessage(EMAIL_ERROR_MSG)
        }
        break

      case CTA_TYPE.ADD_TO_CART:
        if (props.isPostOrderFlow && props.handlePostOrderCallback) {
          props.handlePostOrderCallback()
        } else {
          handleAddToCart(
            false,
            showPurchaseTypeSelector && purchaseOption === PURCHASE_TYPES.SUBSCRIBE && selectedPlanId
              ? selectedPlanId
              : undefined,
          )
        }
        break

      case CTA_TYPE.SHOP_IN_THE_APP:
        handleShopInApp?.({ product: props.product, selectedOptions: props.selectedOptions ?? [], isButtonClick: true })
        break

      default:
        // @i18n-ignore
        console.warn('Invalid type')
    }
  }

  const isDefaultCTAType =
    type !== CTA_TYPE.PREORDER &&
    type !== CTA_TYPE.WAITLIST &&
    type !== CTA_TYPE.OUT_OF_STOCK &&
    type !== CTA_TYPE.SHOP_IN_THE_APP
  const shouldKeepDefaultDesktopExpressPayRowMounted =
    showExpressPay && !props.isQuickATCForMobile && !props.isPostOrderFlow
  const shouldRenderDefaultCTA = isDefaultCTAType || shouldKeepDefaultDesktopExpressPayRowMounted

  let nonDefaultCTAContent: ReactNode = null
  switch (type) {
    case CTA_TYPE.PREORDER: {
      if (props.isPostOrderFlow) {
        nonDefaultCTAContent = (
          <ProductDisplayButtonDS
            onClick={() => undefined}
            disabled
            buttonCss={pointerEventsNoneCss}
            showNewPdpDesignV2={showNewPdpDesignV2}
            product={props.product}
            selectedOptions={props.selectedOptions}
            setIsCartPopUpOpen={props.setIsCartPopUpOpen}
            isBuyNowPdp={props.isBuyNowPdp}
            isFromCartPopup={isFromCartPopup}
          >
            {t('out_of_stock')}
          </ProductDisplayButtonDS>
        )
        break
      }
      revertEmailErrorMsg()
      nonDefaultCTAContent = (
        <div
          data-testid={TEST_LOCATORS.PRODUCT_CTA}
          ref={trackIsVisiblePreorderRef}
          className={cx(buttonWrapperCss, hideKlarna ? hideKlarnaCss : undefined)}
        >
          {errorMessage ? (
            <RenderErrorMessageDS
              errorMessage={errorMessage}
              isQuickATCForMobile={props.isQuickATCForMobile}
              showNewPdpDesignV2={showNewPdpDesignV2}
            />
          ) : null}
          {props.isQuickATCForMobile ? (
            !props.isKeyboardVisibleForQuickATCForMobile && (
              <div className={quickATCWrapperCss}>
                <ProductDisplayButtonDS
                  onClick={handleClick}
                  showLoader={props.isAddToCartInProgress}
                  selectorKey={QUINCE_BOT_LOCATORS.PDP.ADD_TO_CART}
                  showNewPdpDesignV2={showNewPdpDesignV2}
                  product={props.product}
                  selectedOptions={props.selectedOptions}
                  setIsCartPopUpOpen={props.setIsCartPopUpOpen}
                  isBuyNowPdp={props.isBuyNowPdp}
                  isFromCartPopup={isFromCartPopup}
                >
                  {ctaText}
                </ProductDisplayButtonDS>
              </div>
            )
          ) : (
            <ProductDisplayButtonDS
              onClick={handleClick}
              showLoader={props.isAddToCartInProgress}
              selectorKey={QUINCE_BOT_LOCATORS.PDP.ADD_TO_CART}
              showNewPdpDesignV2={showNewPdpDesignV2}
              product={props.product}
              selectedOptions={props.selectedOptions}
              setIsCartPopUpOpen={props.setIsCartPopUpOpen}
              isBuyNowPdp={props.isBuyNowPdp}
              isFromCartPopup={isFromCartPopup}
            >
              {ctaText}
            </ProductDisplayButtonDS>
          )}
        </div>
      )
      break
    }

    case CTA_TYPE.WAITLIST: {
      nonDefaultCTAContent = (
        <ProductWaitlistCTADS
          handleClick={handleClick}
          errorMessage={errorMessage}
          isQuickATCForMobile={props.isQuickATCForMobile}
          isBuyNowInProgress={isBuyNowInProgress}
          isAddToCartInProgress={props.isAddToCartInProgress}
          addToCartRef={addToCartRef}
          isKeyboardVisibleForQuickATCForMobile={props.isKeyboardVisibleForQuickATCForMobile}
          trackIsVisibleWaitlistRef={trackIsVisibleWaitlistRef}
          email={email}
          setEmail={setEmail}
          setIsKeyboardVisibleForQuickATCForMobile={props.setIsKeyboardVisibleForQuickATCForMobile}
          isPostOrderFlow={props.isPostOrderFlow}
          isCurrentProductOptionLeavingSoon={isCurrentProductOptionLeavingSoon}
          selectedVariantPurchaseInfo={selectedVariantPurchaseInfo}
          isQuickATCModal={isQuickATCModal}
          showNewPdpDesignV2={showNewPdpDesignV2}
          isFromCartPopup={isFromCartPopup}
        />
      )
      break
    }

    case CTA_TYPE.OUT_OF_STOCK: {
      nonDefaultCTAContent = (
        <>
          <RenderErrorMessageDS errorMessage={t('out_of_stock')} showNewPdpDesignV2={showNewPdpDesignV2} />
          <ProductDisplayButtonDS
            onClick={() => undefined}
            disabled
            buttonCss={pointerEventsNoneCss}
            showNewPdpDesignV2={showNewPdpDesignV2}
            product={props.product}
            selectedOptions={props.selectedOptions}
            setIsCartPopUpOpen={props.setIsCartPopUpOpen}
            isBuyNowPdp={props.isBuyNowPdp}
            isFromCartPopup={isFromCartPopup}
          >
            {t(atcLabel)}
          </ProductDisplayButtonDS>
        </>
      )
      break
    }

    case CTA_TYPE.SHOP_IN_THE_APP: {
      nonDefaultCTAContent = (
        <>
          {isNonPhysicalProduct ? <NonPhysicalProductDeliveryTextDS className={inStockMsgClass} /> : null}
          {showInStockAndReadyToShip ? <InStockAndReadyToShipDS className={inStockMsgClass} /> : null}
          {selectedVariantPurchaseInfo?.showWaitList ? (
            <RenderErrorMessageDS errorMessage={t('out_of_stock')} showNewPdpDesignV2={showNewPdpDesignV2} />
          ) : null}
          <ProductDisplayButtonDS
            onClick={handleClick}
            buttonType={BUTTON_TYPES.SECONDARY}
            showNewPdpDesignV2={showNewPdpDesignV2}
            product={props.product}
            selectedOptions={props.selectedOptions}
            setIsCartPopUpOpen={props.setIsCartPopUpOpen}
            isBuyNowPdp={props.isBuyNowPdp}
            isFromCartPopup={isFromCartPopup}
          >
            {t('shop_in_the_app')}
          </ProductDisplayButtonDS>
        </>
      )
      break
    }

    default:
      revertEmailErrorMsg()
      break
  }

  // Default (ADD_TO_CART / B2B) CTA. Preserves the ExpressPay mounted-button pattern: when not
  // visible the wrapper keeps `visibility: hidden` (NOT display:none) so the node stays mounted +
  // measurable for ExpressPay, and `aria-hidden` is set on this non-focusable wrapper only — the
  // button inside is removed from the tab order by `visibility: hidden`, so no focusable element is
  // ever hidden from AT.
  const renderDefaultCTA = (isVisible: boolean) => {
    const buyNowCss: SystemStyleObject | undefined = isBuyNowInProgress ? pointerEventsNoneCss : undefined
    return (
      <div
        data-testid={isVisible ? TEST_LOCATORS.PRODUCT_CTA : undefined}
        className={cx(isVisible ? undefined : defaultCTAHiddenCss, hideKlarna ? hideKlarnaCss : undefined)}
        aria-hidden={!isVisible}
      >
        {isVisible && isNonPhysicalProduct ? <NonPhysicalProductDeliveryTextDS className={inStockMsgClass} /> : null}
        {isVisible && showInStockAndReadyToShip ? <InStockAndReadyToShipDS className={inStockMsgClass} /> : null}
        {isVisible && errorMessage ? (
          <RenderErrorMessageDS
            errorMessage={errorMessage}
            isQuickATCForMobile={props.isQuickATCForMobile}
            showNewPdpDesignV2={showNewPdpDesignV2}
            hideIcon={isGoldBarError}
            isBuyNowPdp={props.isBuyNowPdp}
            isFromCartPopup={isFromCartPopup}
          />
        ) : null}

        {isVisible && props.isQuickATCForMobile ? (
          !props.isKeyboardVisibleForQuickATCForMobile && (
            <div className={quickATCWrapperCss}>
              <ProductDisplayButtonDS
                onClick={handleClick}
                showLoader={props.isAddToCartInProgress}
                selectorKey={QUINCE_BOT_LOCATORS.PDP.ADD_TO_CART}
                buttonRef={addToCartRef}
                disabled={isGoldBarError || props.disableAddToCart}
                buttonCss={buyNowCss}
                showNewPdpDesignV2={showNewPdpDesignV2}
                product={props.product}
                selectedOptions={props.selectedOptions}
                setIsCartPopUpOpen={props.setIsCartPopUpOpen}
                isBuyNowPdp={props.isBuyNowPdp}
                isFromCartPopup={isFromCartPopup}
              >
                {t(atcLabel)}
              </ProductDisplayButtonDS>
            </div>
          )
        ) : isVisible && props.isPostOrderFlow ? (
          <div className={postOrderCtaWrapperCss}>
            <ProductDisplayButtonDS
              onClick={handleClick}
              showLoader={props.isAddToCartInProgress}
              selectorKey={QUINCE_BOT_LOCATORS.PDP.ADD_TO_CART}
              buttonRef={addToCartRef}
              disabled={isGoldBarError || disableAddToCart}
              buttonCss={buyNowCss}
              showNewPdpDesignV2={showNewPdpDesignV2}
              product={props.product}
              selectedOptions={props.selectedOptions}
              setIsCartPopUpOpen={props.setIsCartPopUpOpen}
              isBuyNowPdp={props.isBuyNowPdp}
              isFromCartPopup={isFromCartPopup}
            >
              {t('continue_with_this_item')}
            </ProductDisplayButtonDS>
          </div>
        ) : !props.isQuickATCForMobile &&
          !props.isPostOrderFlow &&
          (isVisible || shouldKeepDefaultDesktopExpressPayRowMounted) ? (
          <div
            className={
              showExpressPay ? cx(expressPayRowCss, isApplePayReady ? expressPayRowReadyCss : undefined) : undefined
            }
          >
            <ProductDisplayButtonDS
              onClick={handleClick}
              showLoader={props.isAddToCartInProgress}
              selectorKey={QUINCE_BOT_LOCATORS.PDP.ADD_TO_CART}
              buttonRef={addToCartRef}
              disabled={isGoldBarError || disableAddToCart}
              buttonCss={buyNowCss}
              buttonType={isB2BMode ? BUTTON_TYPES.SECONDARY : undefined}
              showNewPdpDesignV2={showNewPdpDesignV2}
              product={props.product}
              selectedOptions={props.selectedOptions}
              setIsCartPopUpOpen={props.setIsCartPopUpOpen}
              isBuyNowPdp={props.isBuyNowPdp}
              isFromCartPopup={isFromCartPopup}
            >
              {t(atcLabel)}
            </ProductDisplayButtonDS>

            {showExpressPay ? (
              <ExpressPay
                config={expressPayConfig}
                className={isBuyNowInProgress ? pointerEventsNoneClassCss : undefined}
                dataSelectorKey={QUINCE_BOT_LOCATORS.PDP.APPLE_PAY_CLICK}
                productPrice={
                  showPurchaseTypeSelector && purchaseOption === PURCHASE_TYPES.SUBSCRIBE && props.subscriptionPrice
                    ? props.subscriptionPrice
                    : props.salePrice
                }
                onReady={handleApplePayReady}
                enableAnimation={enableExpressPayAnimation}
                disableClick={isGoldBarError || disableAddToCart}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <>
      {nonDefaultCTAContent}
      {shouldRenderDefaultCTA ? renderDefaultCTA(isDefaultCTAType) : null}
      {isBisWaitlistSmsEnabled ? (
        <WaitlistSmsModal
          open={showSmsModal}
          onClose={() => {
            setShowSmsModal(false)
          }}
          onSubmitPhone={onSubmitPhone}
        />
      ) : null}
    </>
  )
}

/**
 * ProductCTADS default export — twin of legacy `ProductCTAWrapper`. Renders the holiday message and
 * the EDD slot (`EDDMessageContainerDS` twin) followed by the CTA region, mirroring the legacy
 * wrapper. The holiday-shown signal is computed here (verbatim with legacy) and threaded into the
 * EDD container as `holidayMessageShown`, which drives `shouldApplyCutoffStyle`.
 */
const ProductCTADSWrapper = (props: ProductCTAType) => {
  const { isGoldBarError } = useGoldBarProductEligibility(props.product)
  const { locale } = useLocaleContext()
  const holidayConfig = useStatsigDynamicHolidayConfig()
  const eddExp = useStatsigExperiment(STATSIG_EXPERIMENTS.EDD)
  const { deviceType } = useDeviceInfo()
  const isMobile = deviceType === DEVICE_TYPES.MOBILE

  // Holiday-message gate — mirrors legacy ProductCTAWrapper. Same base messaging conditions as the
  // in-stock message, then checks whether any displayTag maps to an enabled holiday config entry.
  const baseMessagingConditions = getBaseMessagingConditions(
    props.selectedVariantPurchaseInfo,
    props.showVariantAvailableInventory,
    props.product,
    props.areAllVariantOOS ?? false,
    isGoldBarError,
    eddExp.init,
  )

  const hasDisplayTags = hasValidDisplayTags(props.selectedVariantPurchaseInfo?.eddDetail?.displayTags, holidayConfig)

  let showHolidayMessage = false
  if (baseMessagingConditions && hasDisplayTags && props.selectedVariantPurchaseInfo?.eddDetail?.displayTags) {
    showHolidayMessage = checkHolidayMessageShown(
      props.selectedVariantPurchaseInfo.eddDetail.displayTags,
      holidayConfig,
      locale,
    )
  }

  // EDD-visibility gate copied verbatim from legacy ProductCTAWrapper.
  const showEDDMsg = productHelper.getIsAgeRestrictedProduct(props.product as Product)
    ? props.type === CTA_TYPE.ADD_TO_CART
    : !(
        props.type === CTA_TYPE.WAITLIST ||
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- parity with legacy boolean OR
        props.selectedVariantPurchaseInfo?.isPreorder ||
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- parity with legacy boolean OR
        props.selectedVariantPurchaseInfo?.showMadeToOrder ||
        getIsGiftCard(props.product?.id) ||
        props.product?.isMadeToOrder
      )

  return (
    <>
      {showHolidayMessage && !props.isPostOrderFlow ? (
        <HolidayMessageDS className={props.inStockMsgClass} eddDetail={props.selectedVariantPurchaseInfo?.eddDetail} />
      ) : null}
      {/*
        EDD region: DS path renders the `EDDMessageContainerDS` twin in the same slot/gate the
        legacy wrapper used for `EDDMessageContainer`. Prop-compatible drop-in. `holidayMessageShown`
        is threaded through so the container's `shouldApplyCutoffStyle` matches legacy behavior.
      */}
      {showEDDMsg && props.product && !isGoldBarError && !props.isPostOrderFlow ? (
        <EDDMessageContainerDS
          eddDetail={props.selectedVariantPurchaseInfo?.eddDetail}
          product={props.product}
          selectedInternalVariantId={props.selectedVariantPurchaseInfo?.selectedInternalVariantId}
          isQuickATCForMobile={props.isQuickATCForMobile}
          showIcon={isMobile}
          holidayMessageShown={showHolidayMessage}
        />
      ) : null}
      <ProductCTADS {...props} />
      <div ref={props.atcSectionEndRef} data-testid={TEST_LOCATORS.PRODUCT_CTA_SECTION_END} />
    </>
  )
}

export { ProductCTADS }
export default ProductCTADSWrapper
